import {useState, useMemo, useCallback, useEffect, useRef} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useFields} from "./useFields";
import {DataTableSortStatus} from "mantine-datatable";
import {
  compareSortStatus,
  IdeasUrlState,
  parseIdeasUrlState,
  serializeIdeasUrlState,
} from "../utils/ideasUrlState";
import {useIdeaFilters} from "./useIdeaFilters";
import {useIdeasStore} from "../components/ideas/store/ideasStore";
import {DateRange, GlobalFilters, EvidenceInclusion} from "../components/ideas/store/types";

interface UseIdeasUrlReturn {
  page: string;
  selectedView: any;
  sortStatus: DataTableSortStatus | null;
  globalFilters: GlobalFilters;
  tabFilters: any[];
  evidenceInclusion: EvidenceInclusion;
  setPage: any;
  setSelectedView: any;
  setSortStatus: any;
  setGlobalFilters: any;
  setTabFilters: any;
  setEvidenceInclusion: (value: EvidenceInclusion) => void;
  setOwner: (value: string[] | null) => void;
  setProductArea: (value: string[] | null) => void;
  setDateRange: (value: DateRange | null) => void;
}

export const useIdeasUrlState = ({ subPageKey }: { subPageKey?: string }): UseIdeasUrlReturn => {
  const location = useLocation();
  const navigate = useNavigate();

  const { ideaDefaultSortColumn, subPages } = useFields();
  const { ideasFiltersConfig } = useIdeaFilters();

  // Use Zustand selectors to make these reactive
  // Provide fallback defaults in case store state is undefined (e.g., during hydration)
  const globalFilters = useIdeasStore((state) => state?.globalFilters ?? { owner: undefined, productArea: undefined, dateRange: null });
  const evidenceInclusion = useIdeasStore((state) => state?.evidenceInclusion ?? 'total');

  // Initialize from URL so first render has correct selectedView/page (fixes selectedView=All never loading)
  const [selectedView, setSelectedView] = useState<string | null>(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    return params.get('selectedView') || null;
  });
  const [page, setPage] = useState<string>(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    return params.get('page') || '1';
  });
  const [sortStatus, setSortStatusState] = useState<DataTableSortStatus | null>(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const sortStr = params.get('sortStatus');
    if (!sortStr) return null;
    try {
      const parsed = JSON.parse(sortStr);
      if (parsed?.columnAccessor && parsed.columnAccessor !== 'null') return parsed;
    } catch (_) { /* ignore */ }
    return null;
  });
  const [tabFilters, setTabFilters] = useState<any[]>([]);

  // Refs to track previous values and prevent unnecessary URL updates
  const prevStateRef = useRef<{
    page: string;
    selectedView: string | null;
    sortStatus: DataTableSortStatus | null;
    globalFilters: IdeasUrlState['globalFilters'];
    tabFilters: any[];
    evidenceInclusion: EvidenceInclusion;
  }>({
    page: '',
    selectedView: null,
    sortStatus: null,
    globalFilters: { owner: undefined, productArea: undefined, dateRange: null },
    tabFilters: [],
    evidenceInclusion: 'total'
  });
  const isInitialMount = useRef(true);
  const isUpdatingFromUrl = useRef(false);
  
  // Memoize default sort status to prevent creating new objects
  const defaultSortStatus = useMemo(() => {
    // Check for subpage-specific default sorting first
    if (subPageKey) {
      const subPage = subPages.find((page: any) => page.key === subPageKey);
      if (subPage?.defaultSortColumn) {
        return { columnAccessor: subPage.defaultSortColumn, direction: "desc" as const };
      }
    }

    // Fallback to global default sort column from configuration
    if (ideaDefaultSortColumn) {
      return { columnAccessor: ideaDefaultSortColumn, direction: "desc" as const };
    }

    // Final fallback to accountsCount (before config loads)
    return { columnAccessor: "aggregations.accountsCount.total", direction: "desc" as const };
  }, [subPageKey, subPages, ideaDefaultSortColumn]);

  const handleSortStatus = useCallback((sortStatus: DataTableSortStatus | null): DataTableSortStatus => {
    if (sortStatus) {
      return sortStatus;
    }
    return defaultSortStatus;
  }, [defaultSortStatus]);

  useEffect(() => {
    // Skip if we're updating URL ourselves to avoid cycles
    if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      return;
    }

    const {
      page,
      selectedView: selectedViewName,
      sortStatus,
      globalFilters: urlGlobalFilters,
      tabFilters: urlTabFilters,
      evidenceInclusion: parsedEvidenceInclusion
    }: IdeasUrlState = parseIdeasUrlState(location.search, ideasFiltersConfig);
    
    const searchParams = new URLSearchParams(location.search);
    const hasEvidenceInclusionInUrl = searchParams.has("evidenceInclusion");
    
    // Check if URL has global filter parameters
    const hasOwnerInUrl = searchParams.has("global.owner");
    const hasProductAreaInUrl = searchParams.has("global.productArea");
    const hasDateRangeInUrl = searchParams.has("global.dateRange");
    
    // Get persisted values from store
    const persistedFilters = useIdeasStore.getState().getCurrentGlobalFilters();
    const persistedEvidenceInclusion = useIdeasStore.getState().getCurrentEvidenceInclusion();
    
    const globalFilters: GlobalFilters = {
      owner: hasOwnerInUrl ? urlGlobalFilters.owner : persistedFilters.owner,
      productArea: hasProductAreaInUrl ? urlGlobalFilters.productArea : persistedFilters.productArea,
      dateRange: hasDateRangeInUrl ? urlGlobalFilters.dateRange : persistedFilters.dateRange,
    };

    const currentStoreEvidenceInclusion = useIdeasStore.getState().evidenceInclusion;
    const evidenceInclusion: EvidenceInclusion = hasEvidenceInclusionInUrl 
      ? parsedEvidenceInclusion 
      : (currentStoreEvidenceInclusion !== persistedEvidenceInclusion 
        ? currentStoreEvidenceInclusion
        : persistedEvidenceInclusion);
    
    // Only update state if values actually changed
    if (prevStateRef.current.page !== page) {
      setPage(page);
    }
    if (prevStateRef.current.selectedView !== selectedViewName) {
      setSelectedView(selectedViewName || null);
    }
    
    const newSortStatus = handleSortStatus(sortStatus);
    if (!compareSortStatus(prevStateRef.current.sortStatus, newSortStatus)) {
      setSortStatusState(newSortStatus);
    }
    
    if (JSON.stringify(prevStateRef.current.globalFilters) !== JSON.stringify(globalFilters)) {
      useIdeasStore.getState().setGlobalFilters(globalFilters);
    }
    
    const urlTabJson = JSON.stringify(urlTabFilters || []);
    const stateTabJson = JSON.stringify(tabFilters || []);
    const prevTabJson = JSON.stringify(prevStateRef.current.tabFilters || []);

    const urlDiffersFromState = urlTabJson !== stateTabJson;
    const urlDiffersFromPrev = urlTabJson !== prevTabJson;
    // Only treat as "clear in flight" when state was emptied after previously having
    // tab filters — not on initial mount ([] state + URL filters is hydration).
    const isClearInFlight =
      (tabFilters || []).length === 0 &&
      (urlTabFilters || []).length > 0 &&
      (prevStateRef.current.tabFilters || []).length > 0;

    if (urlDiffersFromState && urlDiffersFromPrev && !isClearInFlight) {
      setTabFilters(urlTabFilters || []);
    }

    if (currentStoreEvidenceInclusion !== evidenceInclusion) {
      useIdeasStore.getState().setEvidenceInclusion(evidenceInclusion);
    }
    
    // Update ref
    prevStateRef.current = {
      page,
      selectedView: selectedViewName || null,
      sortStatus: newSortStatus,
      globalFilters,
      tabFilters: isClearInFlight ? [] : (urlTabFilters || []),
      evidenceInclusion
    };
  }, [location.search, ideasFiltersConfig, subPageKey, subPages, ideaDefaultSortColumn, tabFilters]);

  useEffect(() => {
    // Skip on initial mount to avoid unnecessary navigation
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevStateRef.current = {
        page,
        selectedView,
        sortStatus,
        globalFilters,
        tabFilters,
        evidenceInclusion
      };
      return;
    }

    const currentSortStatus = sortStatus;
    
    // Check if anything actually changed by comparing with previous state
    const sortStatusChanged = !compareSortStatus(prevStateRef.current.sortStatus, currentSortStatus);
    const pageChanged = prevStateRef.current.page !== page;
    const selectedViewChanged = prevStateRef.current.selectedView !== selectedView;
    const globalFiltersChanged = JSON.stringify(prevStateRef.current.globalFilters) !== JSON.stringify(globalFilters);
    const tabFiltersChanged = JSON.stringify(prevStateRef.current.tabFilters) !== JSON.stringify(tabFilters);
    const evidenceInclusionChanged = prevStateRef.current.evidenceInclusion !== evidenceInclusion;
    
    console.log('🔍 [useIdeasUrlState] URL update check:', {
      globalFiltersChanged,
      globalFilters,
      prevGlobalFilters: prevStateRef.current.globalFilters,
      dateRange: globalFilters?.dateRange
    });
    
    // Only update URL if something actually changed
    if (!pageChanged && !selectedViewChanged && !sortStatusChanged && !globalFiltersChanged && !tabFiltersChanged && !evidenceInclusionChanged) {
      return;
    }

    // Update ref with new values
    prevStateRef.current = {
      page,
      selectedView,
      sortStatus: currentSortStatus,
      globalFilters,
      tabFilters,
      evidenceInclusion
    };

    const newQueryString = serializeIdeasUrlState({
      page,
      selectedView: selectedView,
      sortStatus: currentSortStatus,
      globalFilters,
      tabFilters,
      evidenceInclusion,
    });

    const nextUrl = `${location.pathname}?${newQueryString}`;
    const currentUrl = `${location.pathname}${location.search}`;
    
    if (currentUrl !== nextUrl) {
      isUpdatingFromUrl.current = true;
      navigate(nextUrl, { replace: true });
    }
  }, [
    page,
    selectedView,
    sortStatus?.columnAccessor,
    sortStatus?.direction,
    globalFilters,
    tabFilters,
    evidenceInclusion,
    location.pathname,
    navigate
  ]);


  const setSortStatus = useCallback((newSortStatus: DataTableSortStatus | null | ((prev: DataTableSortStatus | null) => DataTableSortStatus | null)) => {
    if (typeof newSortStatus === 'function') {
      setSortStatusState((prev) => {
        const result = newSortStatus(prev);
        if (compareSortStatus(prev, result)) {
          return prev;
        }
        return result;
      });
    } else {
      if (!compareSortStatus(sortStatus, newSortStatus)) {
        setSortStatusState(newSortStatus);
      }
    }
  }, [sortStatus]);

  // Get setters from store (these are stable functions, so getState is fine)
  const setGlobalFilters = useIdeasStore((state) => state.setGlobalFilters);
  const setEvidenceInclusion = useIdeasStore((state) => state.setEvidenceInclusion);
  const setOwner = useIdeasStore((state) => state.setOwner);
  const setProductArea = useIdeasStore((state) => state.setProductArea);
  const setDateRange = useIdeasStore((state) => state.setDateRange);

  return {
    page,
    selectedView,
    sortStatus,
    globalFilters,
    tabFilters,
    evidenceInclusion,
    setPage,
    setSelectedView,
    setSortStatus,
    setGlobalFilters,
    setTabFilters,
    setEvidenceInclusion,
    setOwner,
    setProductArea,
    setDateRange,
  };
}; 